import Drawer from "app/pages/.shared/Drawer/Drawer";
import PropTypes from "prop-types";
import Button, { BUTTON_VARIANTS, BUTTON_SIZES } from "app/pages/.shared/form/Button";
import { FormattedMessage } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import IconBack from "app/pages/.shared/static/icons/IconBack";
import "./ProductFilterDrawer.scss";
import { Formik, Form, Field } from "formik";
import {
	ProductFilterCollapsible,
	ProductFilterCollapsiblePanel,
} from "app/features/filters/productFilters/components/ProductFilterCollapsible";
import RadioFormik from "app/pages/.shared/form/RadioFormik";
import Separator from "app/pages/.shared/Separator";
import { Fragment, useState } from "react";
import ProductFilterChip from "./ProductFilterChip";
import CheckboxFormik from "app/pages/.shared/form/CheckboxFormik";
import { FILTER_SELECTION_TYPES } from "../constants/filterSelectionTypes";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

const ProductFilterDrawer = ({
	isOpen,
	onClose,
	facets,
	resultsCount,
	selectedFilters,
	hasActiveFilters,
	onFilterChange,
	onResetFilters,
	onDeleteFilterOption,
	autoClose,
	defaultOpenKey,
}) => {
	const { track } = useAnalytics();
	const [activeKey, setActiveKey] = useState(
		autoClose ? defaultOpenKey || null : [defaultOpenKey || null]
	);

	const handlePanelChange = key => {
		if (autoClose) {
			setActiveKey(key ?? null);
		} else {
			const keys = Array.isArray(key) ? key : [key];
			setActiveKey(keys);
		}
	};

	const closeAllPanels = () => {
		setTimeout(() => setActiveKey(null), 0);
	};

	return (
		<Drawer
			drawerClassName="product-filter-drawer"
			isOpen={isOpen}
			onClose={onClose}
			width={"400"}
			direction="rtl"
		>
			<Formik
				initialValues={selectedFilters}
				onSubmit={values => {
					track("product-filter-apply", values);
					onClose();
				}}
				enableReinitialize
			>
				{({ values }) => {
					return (
						<Form className="product-filter-drawer__form">
							<header className="product-filter-drawer__header">
								<Button
									onClick={onClose}
									icon={<IconBack />}
									className="product-filter-drawer__close-button"
								/>

								<Typography
									variant={TYPOGRAPHY_VARIANTS.XL}
									component="h2"
									isBold
									className="product-filter-drawer__title"
								>
									<FormattedMessage id="filter.mobile.button.label" />
								</Typography>

								{hasActiveFilters && (
									<Button
										onClick={onResetFilters}
										className="product-filter-drawer__reset-button"
										variant={BUTTON_VARIANTS.BORDERLESS}
									>
										<Typography
											variant={TYPOGRAPHY_VARIANTS.SMALL}
											component="span"
											isBold
											className="product-filter-drawer__reset-button"
										>
											<FormattedMessage id="general.filter.raz.label" />
										</Typography>
									</Button>
								)}
							</header>

							<section className="product-filter-drawer__body">
								{hasActiveFilters && (
									<>
										<ul className="product-filter-drawer__selected-filters">
											{Object.entries(selectedFilters).map(
												([key, values]) => {
													return values.map(value => {
														const label = facets[key]?.options?.find(
															option => option.value === value
														)?.label;

														if (!label) {
															return null;
														}

														return (
															<li
																key={`${key}-${value}`}
																className="product-filter-drawer__selected-filter"
															>
																<ProductFilterChip
																	label={label}
																	onRemove={() =>
																		onDeleteFilterOption(
																			key,
																			value
																		)
																	}
																/>
															</li>
														);
													});
												}
											)}
										</ul>

										<Separator design="full" />
									</>
								)}

								<ProductFilterCollapsible
									accordion={autoClose}
									activeKey={activeKey}
									onChange={handlePanelChange}
								>
									{Object.entries(facets).map(([key, facet], index) => {
										const selectedValues = facet.options
											.filter(({ value }) =>
												selectedFilters?.[key]?.includes(value)
											)
											.map(option => option?.label);

										return (
											<Fragment key={key}>
												<ProductFilterCollapsiblePanel
													title={
														<Typography
															variant={TYPOGRAPHY_VARIANTS.LARGE}
															isBold
															component="h3"
														>
															<FormattedMessage id={facet.labelKey} />

															{facet.selectionMode ===
																FILTER_SELECTION_TYPES.MULTIPLE &&
																selectedValues.length > 0 &&
																` (${selectedValues.length})`}
														</Typography>
													}
													{...facet.selectionMode ===
														FILTER_SELECTION_TYPES.SINGLE && {
														selectedFilter: selectedValues[0],
													}}
													key={key}
												>
													{facet.options.map(
														({ value, label, count }) => {
															if (
																facet.selectionMode ===
																FILTER_SELECTION_TYPES.SINGLE
															) {
																return (
																	<RadioFormik
																		key={`${label}-${count}`}
																		id={`${label}-${count}`}
																		className="product-filter-drawer__radio"
																		showIcon={true}
																		name={key}
																		toggle={() => {
																			onFilterChange(
																				key,
																				value
																			);

																			track(
																				"pp-filter-facet-clicked",
																				{
																					facetName: key,
																					facetValue: value,
																				}
																			);

																			if (autoClose) {
																				closeAllPanels();
																			}
																		}}
																		value={String(value)}
																		checked={{
																			shouldCheckTheValue: true,
																			value: values?.[
																				key
																			]?.includes(
																				String(value)
																			),
																		}}
																		disabled={count === 0}
																	>
																		<Typography
																			variant={
																				TYPOGRAPHY_VARIANTS.REGULAR
																			}
																			component="span"
																		>
																			{label}
																		</Typography>

																		<Typography
																			variant={
																				TYPOGRAPHY_VARIANTS.SMALL
																			}
																			component="span"
																			isBold
																		>
																			{count}
																		</Typography>
																	</RadioFormik>
																);
															}

															return (
																<Field
																	key={`${label}-${count}`}
																	id={`${label}-${count}`}
																	name={key}
																	component={CheckboxFormik}
																	value={String(value)}
																	grouped={true}
																	className="product-filter-drawer__checkbox"
																	onUpdate={(value, field) => {
																		onFilterChange(
																			field,
																			value
																		);

																		track(
																			"pp-filter-facet-clicked",
																			{
																				facetName: key,
																				facetValue: value,
																			}
																		);

																		if (autoClose) {
																			closeAllPanels();
																		}
																	}}
																	disabled={count === 0}
																>
																	<Typography
																		variant={
																			TYPOGRAPHY_VARIANTS.SMALL
																		}
																		component="span"
																	>
																		{label}
																	</Typography>

																	<Typography
																		variant={
																			TYPOGRAPHY_VARIANTS.SMALL
																		}
																		component="span"
																		isBold
																	>
																		{count}
																	</Typography>
																</Field>
															);
														}
													)}
												</ProductFilterCollapsiblePanel>

												{index < Object.entries(facets).length - 1 && (
													<Separator design="full" />
												)}
											</Fragment>
										);
									})}
								</ProductFilterCollapsible>
							</section>

							<footer className="product-filter-drawer__footer">
								<Button
									type="submit"
									className="product-filter-drawer__submit-button"
									size={BUTTON_SIZES.LARGE}
								>
									<Typography
										variant={TYPOGRAPHY_VARIANTS.LARGE}
										component="span"
										isBold
										className="product-filter-drawer__submit-text"
									>
										<FormattedMessage
											id="product.filter.submit"
											values={{ count: resultsCount }}
										/>
									</Typography>
								</Button>
							</footer>
						</Form>
					);
				}}
			</Formik>
		</Drawer>
	);
};

ProductFilterDrawer.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	facets: PropTypes.objectOf(
		PropTypes.shape({
			labelKey: PropTypes.string.isRequired,
			values: PropTypes.arrayOf(
				PropTypes.shape({
					value: PropTypes.string.isRequired,
					label: PropTypes.string.isRequired,
					count: PropTypes.number.isRequired,
				})
			).isRequired,
			selectionMode: PropTypes.oneOf(Object.values(FILTER_SELECTION_TYPES)).isRequired,
		})
	),
	resultsCount: PropTypes.number.isRequired,
	selectedFilters: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
	hasActiveFilters: PropTypes.bool,
	onFilterChange: PropTypes.func.isRequired,
	onResetFilters: PropTypes.func.isRequired,
	onDeleteFilterOption: PropTypes.func.isRequired,
	autoClose: PropTypes.bool,
	defaultOpenKey: PropTypes.string,
};

export default ProductFilterDrawer;
